import type { CSSProperties } from 'react';
import { APP_LOGOS, logoStyle, type AppLogoSlug } from './appLogos';
import { useGradientId } from './terminalIcons';

/**
 * The logo of an application (appLogos.ts) as an icon of the registry, under
 * its slug (iconBundle.tsx): 1em on a 24x24 viewBox, decorative, in its own
 * colour — the ink `logoInks` gives it for the page's mode, through
 * `.waadoo-app-logo` (styles.css). Set inline, so that the colour a
 * container gives its icons (the home page's tile, the project header's
 * `.waadoo-page-header__icon`) does not recolour it: an inline declaration
 * beats any rule of the stylesheet that is not `!important`. A lettered logo
 * (OpenCloud, Tuleap) draws its letters on the same viewBox.
 */
export function AppLogoIcon(props: { slug: AppLogoSlug }) {
  const logo = APP_LOGOS[props.slug];
  const style: CSSProperties = {
    ...logoStyle(props.slug),
    color: 'var(--waadoo-app-logo-ink)',
  };
  return (
    <svg
      className="waadoo-app-logo"
      data-app-logo={props.slug}
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      fill="currentColor"
      style={style}
      aria-hidden="true"
      focusable="false"
    >
      {logo.kind === 'path' ? (
        <path d={logo.path} />
      ) : (
        <text
          x="12"
          y="12"
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={logo.letters.length > 1 ? 11 : 16}
          fontWeight={800}
        >
          {logo.letters}
        </text>
      )}
    </svg>
  );
}

/**
 * The way into an environment's instance, its web address (the registry's
 * `<slug>-instance` keys): a blue globe, with a white badge carrying an
 * « open » arrow ↗ — a page of the web, opened elsewhere. The same drawing
 * as the Odoo module's own (its `webIcons.tsx`), which the core cannot import
 * (.eslintrc.js): nothing in it is Odoo's.
 *
 * In colour, like the terminal icons (terminalIcons.tsx): inline, 1em,
 * decorative, with fixed colours and never `currentColor`, so the link
 * around it does not recolour it.
 */
export function AppInstanceIcon() {
  const gradientId = useGradientId('waadoo-app-instance-gradient');
  return (
    <svg
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#38bdf8" />
          <stop offset="1" stopColor="#2563eb" />
        </linearGradient>
      </defs>
      <circle cx="11" cy="13" r="9" fill={`url(#${gradientId})`} />
      {/* A meridian and three parallels. */}
      <g fill="none" stroke="#fff" strokeWidth="1.4">
        <ellipse cx="11" cy="13" rx="3.8" ry="9" />
        <path d="M2.2 13h17.6M3.6 8.5h14.8M3.6 17.5h14.8" />
      </g>
      {/* The badge, over the globe's top right, and its ↗ arrow. */}
      <circle cx="19" cy="5" r="4.6" fill="#fff" />
      <path
        d="M17.2 6.8l3.4-3.4M18.1 3.4h2.5v2.5"
        fill="none"
        stroke="#2563eb"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
